<template>
    <SidePanel
        :visible="visible"
        :title="capitalizedOptionType + 's'"
        size="panel-md"
        :align-left="alignLeft"
        @close="closePanel"
    >
        <div class="d-flex flex-column">
            <div v-if="currentPanelType === 'view'">
                <TableList
                    :records="localOptions"
                    :fields="fields"
                    :other-buttons="otherButtons"
                    :has-delete="true"
                    :record-type="capitalizedOptionType"
                    :delete-description="`Are you sure you want to delete this ${optionTitle}? All associated audits will lose their ${optionTitle} information.`"
                    :delete-disabled-state="
                        this.endpoint === 'audit-area'
                            ? deleteDisabledState
                            : false
                    "
                    @edit="(record) => editOption({ option: record })"
                    @delete="deleteOption"
                >
                </TableList>

                <div class="d-flex justify-content-end gap-2">
                    <button class="btn btn-primary" @click="createOption">
                        Add Another {{ capitalizedOptionType }}
                        <i :class="`ml-1 fi fi-add`"></i>
                    </button>
                </div>
            </div>

            <OptionForm
                v-else-if="
                    currentPanelType === 'edit' || currentPanelType === 'create'
                "
                :option="currentOption"
                :option-title="optionTitle"
                :endpoint="endpoint"
                :newEntry="currentPanelType == 'create'"
                @allOptions="currentPanelType = 'view'"
                @submit="handleOptionFormSubmit"
            />
        </div>
    </SidePanel>
</template>

<script>
import SidePanel from '../SidePanel.vue';
import TableList from '../TableList.vue';
import OptionForm from './OptionForm.vue';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';

export default {
    name: 'OptionPanel',
    components: {
        TableList,
        SidePanel,
        OptionForm,
    },
    props: {
        visible: {
            Type: Boolean,
            default: false,
        },
        optionTitle: {
            type: String,
            required: true,
        },
        endpoint: {
            type: String,
            required: true,
        },
        alignLeft: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            currentPanelType: 'view',
            currentOption: null,
            localOptions: [],
            siteStore: null,
            areaStore: null,
        };
    },
    emits: ['submit'],
    computed: {
        capitalizedOptionType() {
            return (
                this.optionTitle.charAt(0).toUpperCase() +
                this.optionTitle.slice(1)
            );
        },
        fields() {
            const baseFields = [
                {
                    key: 'name',
                    label: this.capitalizedOptionType,
                    type: 'name',
                    sortable: false,
                    width: '100%',
                },
            ];

            return baseFields;
        },
        otherButtons() {
            const editButton = {
                label: null,
                icon: 'fi-edit',
                class: 'icon-btn-light',
                click: 'edit',
            };

            if (this.endpoint === 'audit-area') {
                editButton.disabledState = (record) => {
                    if (record.company_id === 0) {
                        return {
                            disabled: true,
                            tooltip:
                                'Your company does not own this audit area',
                        };
                    }
                };
            }

            return [editButton];
        },
        isSiteEndpoint() {
            return this.endpoint === 'company-site';
        },
    },
    watch: {
        visible(newVal) {
            if (newVal) {
                this.getOptions();
            }
        },
    },
    methods: {
        closePanel() {
            this.$emit('close');
            this.currentPanelType = 'view';
            this.currentOption = null;
        },
        editOption({ option: option }) {
            this.currentOption = option;
            this.currentPanelType = 'edit';
        },
        createOption() {
            this.currentOption = null;
            this.currentPanelType = 'create';
        },
        handleOptionFormSubmit() {
            this.getOptions(true);
            this.currentPanelType = 'view';
            this.$emit('submit', this.optionTitle);
        },
        deleteOption(option) {
            this.$deleteData(`${this.endpoint}/delete`, { id: option.id }).then(
                (response) => {
                    if (response?.success) {
                        this.$emit('submit', this.optionTitle);
                        this.handleOptionFormSubmit();
                    } else {
                        this.$alertStore.pushError(response?.message);
                    }
                }
            );
        },
        getOptions(forceRefresh = false) {
            if (this.isSiteEndpoint && this.siteStore) {
                if (forceRefresh) {
                    this.siteStore.fetchSites().then(() => {
                        this.localOptions = this.siteStore.sites;
                    });
                } else {
                    this.siteStore.syncSites().then(() => {
                        this.localOptions = this.siteStore.sites;
                    });
                }
            } else if (this.endpoint == 'audit-area' && this.areaStore) {
                if (forceRefresh) {
                    this.areaStore.fetchAreas().then(() => {
                        this.localOptions = this.areaStore.areas;
                    });
                } else {
                    this.areaStore.syncAreas().then(() => {
                        this.localOptions = this.areaStore.areas;
                    });
                }
            } else {
                this.$getData(`${this.endpoint}/read`, {
                    fields: [
                        'id',
                        'name',
                        'address',
                        'city',
                        'province',
                        'country',
                        'ipac_lead_id',
                        'booking_contact_id',
                        'company_id',
                    ],
                }).then((response) => {
                    if (!response?.success) {
                        this.$alertStore.pushError(response.message);
                        return;
                    }

                    this.localOptions = response.data;
                });
            }
        },
        deleteDisabledState(record) {
            if (record.company_id === 0 && this.endpoint === 'audit-area') {
                return {
                    disabled: true,
                    tooltip: 'Your company does not own this audit area',
                };
            }
        },
    },
    created() {
        this.siteStore = useSiteStore();
        this.areaStore = useAreaStore();
    },
};
</script>
